import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle, Circle, Loader2, ArrowRight, Banknote } from 'lucide-react';
import { api } from '../services/api';

/**
 * Seller readiness checklist. Renders inline (when `inline` true) or as a modal.
 * Returns `ready` flag via `onReady` callback once all items are ticked.
 */
export const SellerReadiness = ({ open, onClose, inline = false, onReady }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.getSellerReadiness();
      setData(res.data);
      if (res.data?.ready && onReady) onReady();
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (inline || open) fetch();
  }, [open, inline]);

  const Body = (
    <div className="space-y-4" data-testid="seller-readiness">
      <p className="text-xs text-muted-foreground leading-relaxed">
        Before your Digital Twin can go live on the marketplace, all four steps below must be complete.
        This protects buyers and ensures payouts will reach you the moment a fraction sells.
      </p>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Checking your status...</div>
      ) : (
        <ul className="space-y-2">
          {(data?.checklist || []).map(item => (
            <li key={item.key} className="flex items-start gap-3 p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded" data-testid={`checklist-${item.key}`}>
              {item.ok ? (
                <CheckCircle size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
              ) : (
                <Circle size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${item.ok ? 'text-white' : 'text-amber-400'}`}>{item.label}</p>
                {!item.ok && <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{item.hint}</p>}
              </div>
              {!item.ok && item.action && (
                <Link to={item.action} onClick={onClose}>
                  <Button size="sm" variant="outline" className="border-gold/30 text-gold text-xs hover:bg-gold/10 whitespace-nowrap">
                    {item.key === 'stripe_connect' ? <Banknote size={12} className="mr-1" /> : <ArrowRight size={12} className="mr-1" />}
                    {item.key === 'stripe_connect' ? 'Set up' : 'Go'}
                  </Button>
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      {data?.ready && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400 flex items-center gap-2">
          <CheckCircle size={14} /> You're ready to list on the marketplace.
        </div>
      )}
    </div>
  );

  if (inline) return Body;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg" data-testid="seller-readiness-modal">
        <DialogHeader><DialogTitle className="font-serif text-white">Seller Onboarding Checklist</DialogTitle></DialogHeader>
        {Body}
      </DialogContent>
    </Dialog>
  );
};
